<script setup>
// Adjuntos de un ticket. Al crear (sin ticketId) los archivos elegidos quedan pendientes y los sube
// el alta una vez guardado el ticket; así nunca quedan adjuntos huérfanos. Con el ticket ya creado
// se suben al momento a POST /api/tickets/{id}/documents.
import { ref, watch, onBeforeUnmount } from "vue";
import { ElMessageBox } from "element-plus";
import { Delete, Document, Download, View } from "@element-plus/icons-vue";
import notification from "@/utils/plugins/NotificationService";
import TicketService from "@/api/services/ticket.service";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

const MAX_SIZE = 5 * 1024 * 1024; // mismo límite que el back

const props = defineProps({
  ticketId: { type: [Number, String], default: null },
  documents: { type: Array, default: () => [] },
  pendingFiles: { type: Array, default: () => [] },
  // Asignado o responsable del departamento: puede borrar adjuntos subidos por otros
  canManage: { type: Boolean, default: false },
  enablePreview: { type: Boolean, default: false },
});
const emits = defineEmits(['update:pendingFiles']);

const authStore = useAuthStore();
const uiStore = useUiStore();
const docs = ref([...props.documents]);
watch(() => props.documents, (value) => { closePreview(); docs.value = [...(value || [])]; });
watch(() => props.pendingFiles, (files) => {
  if (previewTarget.value && previewTarget.value.pending && !files.includes(previewTarget.value.file)) closePreview();
});

const previewTarget = ref(null);
const previewUrl = ref('');
const previewKind = ref('');
const previewError = ref('');
const previewBusy = ref(false);
let previewGeneration = 0;
const canPreview = file => /\.(pdf|jpe?g|png)$/i.test(file?.name || '');
function closePreview() {
  previewGeneration++;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = '';
  previewKind.value = '';
  previewError.value = '';
  previewTarget.value = null;
}
onBeforeUnmount(closePreview);

async function togglePreview(target) {
  if (previewBusy.value) return;
  if (previewTarget.value && (target.pending
    ? previewTarget.value.file === target.file
    : !previewTarget.value.pending && previewTarget.value.doc.id === target.doc.id)) {
    closePreview();
    return;
  }
  closePreview();
  const generation = previewGeneration;
  previewTarget.value = target;
  previewBusy.value = true;
  uiStore.startLoading();
  try {
    const response = target.pending ? null : await TicketService.downloadTicketDocument(target.doc.contentUrl);
    const blob = target.pending ? target.file : response.data;
    if (generation !== previewGeneration) return;
    const name = target.pending ? target.file.name : target.doc.name || '';
    const type = (response?.headers?.['content-type'] || blob.type || '').toLowerCase().split(';')[0].trim();
    const fallback = !type || type === 'application/octet-stream';
    const pdf = type === 'application/pdf' || (fallback && /\.pdf$/i.test(name));
    const imageType = ['image/jpeg', 'image/png'].includes(type) ? type
      : fallback && /\.png$/i.test(name) ? 'image/png'
        : fallback && /\.jpe?g$/i.test(name) ? 'image/jpeg' : '';
    if (!pdf && !imageType) {
      previewError.value = target.pending ? "Vista previa no disponible para este tipo de archivo." : "Vista previa no disponible para este tipo de archivo. Descárgalo para verlo.";
      return;
    }
    previewKind.value = pdf ? 'pdf' : 'image';
    previewUrl.value = URL.createObjectURL(new Blob([blob], { type: pdf ? 'application/pdf' : imageType }));
  } catch {
    if (generation === previewGeneration) previewError.value = 'No se ha podido cargar la vista previa. Vuelve a intentarlo.';
  } finally {
    previewBusy.value = false;
    uiStore.stopLoading();
  }
}


const canDelete = (doc) => {
  const user = authStore.getUserLogged;
  if (!user) return false;
  if (user.roles.includes('ROLE_SYSTEMS') || user.roles.includes('ROLE_ADMIN') || props.canManage) return true;
  const uploader = doc.uploadedBy;
  return !!uploader && (typeof uploader === 'object' ? uploader['@id'] : uploader) === user['@id'];
};

const download = async (doc) => {
  let url;
  try {
    const response = await TicketService.downloadTicketDocument(doc.contentUrl);
    url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = doc.name || "adjunto";
    link.click();
  } catch (e) {
    notification.addNotification({ type: 'error', message: 'Error al descargar el adjunto.' });
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
};

const remove = async (doc) => {
  try {
    await ElMessageBox.confirm(`¿Deseas borrar el archivo ${doc.name}?`, 'Confirmar', {
      confirmButtonText: 'Si',
      cancelButtonText: 'No',
      type: 'warning',
    });
  } catch {
    return;
  }
  await uiStore.startLoading();
  try {
    await TicketService.deleteMediaTicket(doc.id);
    if (previewTarget.value?.doc?.id === doc.id) closePreview();
    docs.value = docs.value.filter((d) => d.id !== doc.id);
  } catch (e) {
    notification.addNotification({ type: 'error', message: 'No se ha podido borrar el archivo.' });
  } finally {
    await uiStore.stopLoading();
  }
};

const removePending = (index) => {
  const files = [...props.pendingFiles];
  files.splice(index, 1);
  emits('update:pendingFiles', files);
};

const addFiles = async (files) => {
  const valid = files.filter((file) => file.size <= MAX_SIZE);
  if (valid.length < files.length) {
    notification.addNotification({
      type: 'error',
      message: 'Algún archivo excede el límite de 5 MB y no se ha añadido.',
      duration: 10000
    });
  }
  if (valid.length === 0) return;

  if (!props.ticketId) {
    emits('update:pendingFiles', [...props.pendingFiles, ...valid]);
    return;
  }

  await uiStore.startLoading();
  const failed = [];
  for (const file of valid) {
    try {
      const response = await TicketService.createMediaTicket(props.ticketId, file);
      docs.value.push(response.data);
    } catch (e) {
      failed.push(file.name);
    }
  }
  await uiStore.stopLoading();
  notification.addNotification(failed.length
    ? { type: 'error', message: `No se han podido subir: ${failed.join(', ')}.`, duration: 10000 }
    : { type: 'success', message: 'Archivo cargado correctamente.', duration: 10000 });
};

const selectFiles = () => {
  const fileSelector = document.createElement("input");
  fileSelector.setAttribute("type", "file");
  fileSelector.setAttribute("multiple", "multiple");
  fileSelector.addEventListener("change", (e) => addFiles(Array.from(e.target.files || [])));
  fileSelector.click();
};
</script>

<template>
  <div class="form-item">
    <label>Adjuntos</label>
    <div class="border bg-white h-auto p-2 pl-5 ticket-documents" style="border-radius: 16px;">
      <p v-if="docs.length === 0 && pendingFiles.length === 0">
        No tiene adjuntos asociados
      </p>
      <div v-for="doc in docs" :key="doc.id" class="ticket-documents__entry">
      <div class="ticket-documents__item">
        <el-icon><Document /></el-icon>
        <span v-if="enablePreview">{{ doc.name }}</span><span v-else class="pointer text-primary" @click="download(doc)">{{ doc.name }}</span>
        <button v-if="enablePreview && canPreview(doc)" type="button" class="ticket-documents__preview-button" :disabled="previewBusy" @click="togglePreview({ doc })"><el-icon><View /></el-icon>{{ previewTarget?.doc?.id === doc.id ? 'Ocultar' : 'Ver' }}</button>
        <button v-if="enablePreview" type="button" class="ticket-documents__preview-button" :aria-label="'Descargar ' + doc.name" @click="download(doc)"><el-icon><Download /></el-icon>Descargar</button>
        <el-icon v-if="canDelete(doc)" class="pointer ticket-documents__delete" @click="remove(doc)"><Delete /></el-icon>
      </div>
      <div v-if="previewTarget?.doc?.id === doc.id" class="ticket-documents__preview-area">
        <p v-if="previewError" role="alert" class="ticket-documents__error">{{ previewError }}</p>
        <div v-if="previewUrl" class="ticket-documents__preview">
          <iframe v-if="previewKind === 'pdf'" :src="previewUrl + '#view=FitH'" :title="'Vista previa de ' + doc.name" />
          <img v-else :src="previewUrl" :alt="'Vista previa de ' + doc.name" />
        </div>
      </div>
      </div>
      <div v-for="(file, index) in pendingFiles" :key="index" class="ticket-documents__entry">
      <div class="ticket-documents__item">
        <el-icon><Document /></el-icon>
        <span>{{ file.name }}</span>
        <small class="text-muted">(se subirá al guardar)</small>
        <button v-if="enablePreview && canPreview(file)" type="button" class="ticket-documents__preview-button" :disabled="previewBusy" @click="togglePreview({ pending: true, file })"><el-icon><View /></el-icon>{{ previewTarget?.file === file ? 'Ocultar' : 'Ver' }}</button>
        <el-icon class="pointer ticket-documents__delete" @click="removePending(index)"><Delete /></el-icon>
      </div>
      <div v-if="previewTarget?.file === file" class="ticket-documents__preview-area">
        <p v-if="previewError" role="alert" class="ticket-documents__error">{{ previewError }}</p>
        <div v-if="previewUrl" class="ticket-documents__preview">
          <iframe v-if="previewKind === 'pdf'" :src="previewUrl + '#view=FitH'" :title="'Vista previa de ' + file.name" />
          <img v-else :src="previewUrl" :alt="'Vista previa de ' + file.name" />
        </div>
      </div>
      </div>
    </div>
    <button type="button" class="btn btn_yellow btn-assign mt-2" @click="selectFiles">
      Seleccione...
    </button>
  </div>
</template>

<style scoped>
.ticket-documents__item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.ticket-documents__item { flex-wrap: wrap; min-width: 0; }
.ticket-documents__item span { overflow-wrap: anywhere; min-width: 0; }
.ticket-documents__preview-button { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; padding: 6px 8px; border: 0; background: transparent; color: #474ce6; font-weight: 600; }
.ticket-documents__preview-button:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; border-radius: 8px; }
.ticket-documents__preview-button:disabled { opacity: .5; cursor: not-allowed; }
.ticket-documents__error { color: #d31c5e; margin: 10px 0 0; }
.ticket-documents__preview { width: 100%; min-width: 0; border: 1px solid #d4d9e5; border-radius: 8px; margin-top: 12px; overflow: hidden; }
.ticket-documents__preview iframe { display: block; border: 0; width: 100%; height: 480px; }
.ticket-documents__preview img { display: block; width: 100%; max-height: 480px; object-fit: contain; }
@media (max-width: 767px) { .ticket-documents { padding-left: 16px !important; padding-right: 16px; } }
.ticket-documents__delete {
  color: var(--el-color-danger);
}
</style>
